import { useState, useCallback } from "react";
import request from "superagent";
import { getAuthHeader } from "src/utils/superagent";
import { TimeCardFilesType } from "@src/appV2/FacilityUpcomingCharges/types";
import { isHcpSignature } from "@src/appV2/FacilityUpcomingCharges/useGetTimesheetUrl";
import { TimeCardType } from "@src/appV2/FacilityUpcomingCharges/types/common";
import { isDefined } from "@clipboard-health/util-ts";
import { fixSwappedTimesheetKeysInArray } from "@src/appV2/lib/utils/timesheetFileUtils";

export const getTimesheetDistributionUrl = async (
  fileStorageFileKey: string,
  shiftId: string
): Promise<string> => {
  try {
    const resp = await request
      .get(`${global.api}/shift/${shiftId}/timesheet/${fileStorageFileKey}/distribution-url`)
      .set(await getAuthHeader());
    return resp?.body?.distribution_url || "";
  } catch (error) {
    // oxlint-disable typescript/restrict-template-expressions, no-console
    // eslint-disable-next-line no-console
    console.error(`Not able to get timesheet distribution-url ${error}`);
    // oxlint-enable typescript/restrict-template-expressions, no-console
    throw error;
  }
};

const generateRandomId = () => {
  // crypto.randomUUID was not found by vite bundler
  // FIXME: Automatically disabled during ESLint config update
  // oxlint-disable typescript/prefer-optional-chain
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return self.crypto && (self.crypto as any).randomUUID
    ? // oxlint-enable typescript/prefer-optional-chain
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (self.crypto as any).randomUUID()
    : Math.random().toString(36).slice(-6);
};
interface UploadTimesheetToS3StorageParams {
  file: File;
  shiftId: string;
  authToken?: string;
}

export async function uploadTimesheetToS3Storage(
  params: UploadTimesheetToS3StorageParams
): Promise<string> {
  const { file, shiftId, authToken } = params;
  const authHeader = isDefined(authToken) ? { Authorization: authToken } : await getAuthHeader();
  const randomId = generateRandomId();
  const typeImageSplit = file?.type?.split("image/") || [];
  const fileType = typeImageSplit?.[1] || "png";
  const fileStorageFileKey = `timesheet/${shiftId}/${randomId}.${fileType}`;
  const resp = await request
    .get(`${global.api}/shift/${shiftId}/timesheet/${fileStorageFileKey}/presigned-upload-url`)
    .set(authHeader);
  const preSignedUrl = resp?.body?.presigned_url;
  if (!preSignedUrl) {
    throw new Error("Not able to fetch preSignedUrl");
  }
  try {
    await request.put(preSignedUrl).send(file);
    return fileStorageFileKey;
  } catch (error) {
    // oxlint-disable-next-line typescript/restrict-template-expressions
    throw new Error(`Not able to upload ${error}`);
  }
}

export const uploadFacilityTimeCardTemplate = async (
  facilityId: string,
  file: File
): Promise<{
  folderName: string;
  fileStorageFileKey: string;
  presignedUrl: string;
  distributionUrl: string;
}> => {
  const response = await request
    .get(
      `${global.api}/facilityprofile/${facilityId}/time-card-template/presigned-upload-url?contentType=${file.type}`
    )
    .set(await getAuthHeader());

  await request.put(response.body.presignedUrl).send(file);

  return response.body;
};

export const uploadFacilityImageFss = async (
  facilityId: string,
  file: File
): Promise<{
  folderName: string;
  fileKey: string;
  presignedUrl: string;
}> => {
  const response = await request
    .get(
      `${global.api}/facilityprofile/${facilityId}/facility-image/presigned-upload-url?contentType=${file.type}`
    )
    .set(await getAuthHeader());

  await request.put(response.body.presignedUrl).send(file);

  return response.body;
};

export const useS3Timesheets = () => {
  const [fileList, setFileList] = useState<TimeCardType["files"]>([]);
  const [isLoadFileList, setIsLoadFileList] = useState(false);

  const loadShiftTimesheets = useCallback(
    async (
      files: Array<
        Pick<TimeCardFilesType, "timesheet" | "signedType" | "filename" | "fileStorageFileKey">
      >,
      shiftId: string
    ) => {
      setFileList([]);
      const fixedFiles = fixSwappedTimesheetKeysInArray(files);

      // currently, files may include HCP/HCF signatures
      // so it makes sense to filter them out
      const filteredFiles = (fixedFiles || []).filter((file) => !isHcpSignature(file));
      if (filteredFiles?.length > 0) {
        const s3Requests = filteredFiles.map((file) => {
          return getTimesheetDistributionUrl(file.fileStorageFileKey!, shiftId);
        });
        setIsLoadFileList(true);
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const s3Results: any[] = [];
        const settledS3s = await Promise.allSettled(s3Requests);
        settledS3s.forEach((settle) => {
          if (settle?.status === "fulfilled") {
            s3Results.push(settle.value);
          } else {
            // eslint-disable-next-line no-console
            console.error(`can't load S3TimesheetUrls ${settle?.reason || ""}`);
          }
        });

        setIsLoadFileList(false);
        if (s3Results?.length > 0) {
          setFileList(
            filteredFiles.map((fl, idx) => ({
              ...fl,
              s3Url: s3Results?.[idx],
            }))
          );
        }
      }
    },
    []
  );

  return {
    fileList,
    isLoadFileList,
    loadShiftTimesheets,
  };
};
